import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './Messages.module.scss';
import { parseActivities } from './parseActivities';
import { AiActivityCard } from './AiActivityCard';

export const TypeMarkdown: FC<{ content: string }> = memo(({ content }) => {
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const href = e.currentTarget.getAttribute('href');
      if (!href) return;
      
      try {
        const url = new URL(href);
        
        const productionDomains = [
          'app.maletadoengenheiro.com.br',
          'app.coordly.com.br'
        ];
        
        const isProductionLink = productionDomains.some(domain => 
          url.hostname.includes(domain)
        );
        
        if (isProductionLink || url.hostname === window.location.hostname) {
          e.preventDefault();
          
          const redirectUrl = url.searchParams.get('redirectUrl');
          if (redirectUrl) {
            navigate(decodeURIComponent(redirectUrl));
            return;
          }
          
          navigate(url.pathname + url.search + url.hash);
        }
      } catch {
        if (href.startsWith('/')) {
          e.preventDefault();
          navigate(href);
        }
      }
    },
    [navigate]
  );

  const mdComponents = { a: (props: any) => <a {...props} onClick={handleLinkClick} /> };
  const [displayedContent, setDisplayedContent] = useState('');
  const contentRef = useRef(content);
  const lastContentLengthRef = useRef(0);

  useEffect(() => {
    if (content !== contentRef.current) {
      contentRef.current = content;
      
      if (content.length > lastContentLengthRef.current) {
        setDisplayedContent(content); 
        lastContentLengthRef.current = content.length;
      } else {
        lastContentLengthRef.current = 0;
        setDisplayedContent('');
        
        let currentIndex = 0;
        const typeNextChar = () => {
          if (currentIndex < content.length) {
            currentIndex++;
            setDisplayedContent(content.substring(0, currentIndex));
            setTimeout(typeNextChar, 15); 
          }
        };
        setTimeout(typeNextChar, 50);
      }
    }
  }, [content]);

  useEffect(() => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [displayedContent]);

  const segments = parseActivities(displayedContent);

  return (
    <div className={styles.typingContainer}>
      {segments.map((segment, idx) => {
        if (segment.type === 'activity' && segment.activity) {
          return <AiActivityCard key={idx} {...segment.activity} />;
        }
        return (
          <Markdown key={idx} remarkPlugins={[remarkGfm]} components={mdComponents}>
            {segment.text || ''}
          </Markdown>
        );
      })}
    </div>
  );
});